import { BillingMethodListItem } from 'billing/components/BillingMethodList'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import BillingViewModel from 'jobs/creation/BillingViewModel'
import {
  SubmissionSection,
  SubmissionSectionHeader,
} from '../SubmissionSection'
import { observer } from 'mobx-react'
import * as React from 'react'
import styles from './Billing.sass'

interface BillingProps {
  vm: BillingViewModel
  compact?: boolean
}

/**
 * The Billing view.
 */
export default observer(function Billing({ vm, compact }: BillingProps) {
  return (
    <SubmissionSection compact={compact}>
      <SubmissionSectionHeader
        label="Select a Payment Method"
        compact={compact}
      >
        {vm.selectedPaymentMethod && vm.canChange && (
          <Button
            size={compact ? 'normal' : 'baseline'}
            type="text"
            disabled={vm.disabled}
            onClick={vm.selectMethod}
          >
            Change
          </Button>
        )}
      </SubmissionSectionHeader>
      {vm.selectedPaymentMethod ? (
        <SelectedPaymentMethod vm={vm} />
      ) : (
        <AddNewPaymentMethod vm={vm} />
      )}
    </SubmissionSection>
  )
})

interface SelectedPaymentMethodProps {
  vm: BillingViewModel
}

/**
 " Selected Payment Method
 */
export const SelectedPaymentMethod = observer(function SelectedPaymentMethod({
  vm,
}: SelectedPaymentMethodProps) {
  const method = vm.selectedPaymentMethod!
  return (
    <div className={styles.selectedPaymentMethod}>
      <BillingMethodListItem
        disabled={vm.disabled}
        readOnly
        billingMethod={method}
        data-test="draft-editor-selected-payment-method"
      />
    </div>
  )
})

interface AddNewPaymentMethodProps {
  vm: BillingViewModel
}

/**
 " Add New Payment Method
 */
export const AddNewPaymentMethod = observer(function AddNewPaymentMethod({
  vm,
}: AddNewPaymentMethodProps) {
  return (
    <div className={styles.add}>
      <Button
        block
        type="text"
        disabled={vm.disabled || !vm.canChange}
        onClick={vm.newPaymentMethod}
        data-test="draft-editor-add-payment-method-button"
        title={
          vm.canChange
            ? 'Add a payment method'
            : 'You are not allowed to manage billing.'
        }
      >
        Add Payment Method
      </Button>
    </div>
  )
})

interface BillExternalProps {
  vm: BillingViewModel
}

/**
 * Admin Billing
 *
 * Determines whether the current job's owner payment method is used or not.
 */
export const AdminBilling = observer(function BillExternal({
  vm,
}: BillExternalProps) {
  return (
    <SubmissionSection>
      <label className={styles.billExternal}>
        Bypass billing{' '}
        <Checkbox checked={vm.bypassBilling} onChange={vm.setBypassBilling} />
      </label>
      <p className={styles.billExternalHelp}>
        Should we charge the default billing method or will this be handled
        manually.
      </p>
    </SubmissionSection>
  )
})
